import Head from 'next/head'
import Image from 'next/image'
import styled, { createGlobalStyle } from 'styled-components'

import BuildPassLogo from '../../../public/buildpass.svg'
import { baseInputStyles } from '../shared-components/BUI/Input'

import { DozerLoading } from '@/components/shared-components/DozerLoading'
import { RegionSelector } from '@/modules/account/RegionSelector'
import { colourShift } from '@/utils/colourShift'

export const ClerkStyling = createGlobalStyle`
.cl-rootBox {
  width: 100%;
}

.cl-cardBox {
  border-radius: ${({ theme }) => theme.borderRadius.small};
  box-shadow: none;
  width: 100%;
}

.cl-card {
  background: none;
  border: none;
  box-shadow: none;
  padding: 0;
  border-radius: ${({ theme }) => theme.borderRadius.small};
}

.cl-logoBox {
  height: 2.25rem;
}

.cl-headerTitle {
  ${({ theme }) => theme.font.heading03}
}

.cl-headerSubtitle {
  ${({ theme }) => theme.font.body01}
}

.cl-button {
  ${({ theme }) => theme.font.label02}
  font-weight: 600;
  height: 2.5rem;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  border: 1px solid transparent;
  padding-left: 1rem;
  padding-right: 1rem;
}

.cl-formFieldLabel {
  ${({ theme }) => theme.font.label02}
}

.cl-formButtonPrimary {
  box-shadow: none !important;

  color: ${({ theme }) => theme.colour.theme.buttonTextColour};
  background-color: ${({ theme }) => theme.colour.theme.primaryColour};

  &:hover {
    background-color: ${({ theme }) =>
      colourShift(theme.colour.theme.primaryColour, 20)};
  }

  &:active {
    background-color: ${({ theme }) =>
      colourShift(theme.colour.theme.primaryColour, -30)};
  }
}

.cl-formFieldInput {
  ${baseInputStyles}
  box-shadow: none !important;
  border-width: 1px !important;
  padding: 9px 11px !important;
  line-height: 1.5715 !important;
  max-height: 3rem !important;
}

.cl-socialButtonsBlockButton, .cl-alternativeMethodsBlockButton {
  ${baseInputStyles}
  color: ${({ theme }) => theme.colour.secondary._900};
  background-color: ${({ theme }) => theme.colour.neutral.white};
  box-shadow: none !important;
  padding: 0.5rem 1rem !important;
  font-size: 15px !important;
  border-width: 1px !important;

  &:hover {
    background-color: ${({ theme }) => theme.colour.neutral.surface};
  }

  &:active {
    background-color: ${({ theme }) => theme.colour.secondary._50};
  }

  &:disabled,
  &:disabled:hover,
  &:disabled:active {
    color: ${({ theme }) => theme.colour.secondary._400};
    border-color: ${({ theme }) => theme.colour.secondary._100};
    background-color: transparent;
  }
}

.cl-formFieldSuccessText {
  ${({ theme }) => theme.font.caption01}
  color: ${({ theme }) => theme.colour.primary._500};
}

.cl-formFieldErrorText {
  ${({ theme }) => theme.font.caption01}
  color: ${({ theme }) => theme.colour.semantic.error};
}

.cl-backLink {
  margin-bottom: 1rem;
}
`

const Wrapper = styled.div`
  min-height: 100vh;

  ${({ theme }) =>
    theme.media.sm`
  background-color: ${({ theme }) => theme.colour.neutral.surface};
  `};
  display: flex;
  flex-direction: column;
`

const Header = styled.header`
  height: 90px;
  padding-bottom: 1rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
`

const LogoContainer = styled.div`
  position: relative;
  height: 90px;
  width: 139px;
`

const Main = styled.main`
  flex-grow: 1;
  padding: 0;

  ${({ theme }) => theme.media.sm`
    padding-top: 1rem;
    padding-bottom: 1rem;
  `}

  ${({ theme }) => theme.media.md`
    padding-top: 3rem;
    padding-bottom: 3rem;
  `}

  ${({ theme }) => theme.media.lg`
    padding-top: 5rem;
    padding-bottom: 5rem;
  `}
`

const StyledLogo = styled(Image)`
  max-width: 139px !important;
  max-height: 90px !important;
  object-fit: contain !important;
  margin-left: 1rem !important;

  ${({ theme }) => theme.media.sm`
    margin-left: 2rem !important;
  `}
`

const RegionSelectorContainer = styled.div`
  margin-right: 1rem !important;
`

type Props = {
  loading?: boolean
  children?: React.ReactNode
  logoUrl?: string
  builderName?: string
}

export const AuthPage: React.FC<Props> = ({
  children,
  loading,
  logoUrl,
  builderName,
}) => {
  if (loading) {
    return (
      <Wrapper>
        <Head>
          <title>Loading</title>
        </Head>
        <Header>
          <LogoContainer>
            <StyledLogo
              src={logoUrl ?? BuildPassLogo}
              alt={logoUrl ? `${builderName} logo` : 'BuildPass logo'}
              fill={true}
            />
          </LogoContainer>
          <RegionSelectorContainer>
            <RegionSelector />
          </RegionSelectorContainer>
        </Header>
        <Main>
          <DozerLoading loading />
        </Main>
      </Wrapper>
    )
  }

  return (
    <Wrapper>
      <Header>
        <LogoContainer>
          <StyledLogo
            src={logoUrl ?? BuildPassLogo}
            alt={logoUrl ? `${builderName} logo` : 'BuildPass logo'}
            fill={true}
          />
        </LogoContainer>
        <RegionSelectorContainer>
          <RegionSelector />
        </RegionSelectorContainer>
      </Header>
      <Main>{children}</Main>
    </Wrapper>
  )
}
